import { useEffect, useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Center,
  Container,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconBuildingFactory,
  IconDownload,
  IconUpload,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useTenantStore } from "../../../Boilerplate/AdminTenants/tenantStore";
import { exportPimSchema, getPimSchemas } from "../Pim/pimApi";
import ImportSchemaModal from "../Pim/Schemas/ImportSchemaModal";

export default function SchemaPortabilityAdmin() {
  const { t } = useTranslation("common");
  const allTenants = useTenantStore((state) => state.allTenants);
  const fetchTenants = useTenantStore((state) => state.fetchTenants);

  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [schemas, setSchemas] = useState([]);
  const [schemasLoading, setSchemasLoading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    setTenantsLoading(true);
    // Fetch up to 500 tenants in one go for the picker. This is a hard cap; if the
    // installation grows past it, switch to a server-side searchable picker.
    // TODO: server-side search when tenant count justifies it.
    fetchTenants(1, 500).finally(() => setTenantsLoading(false));
  }, []);

  const tenantOptions = (allTenants?.data || []).map((t) => ({
    value: t.id,
    label: t.name,
  }));

  const loadSchemas = async (tenantId) => {
    if (!tenantId) {
      setSchemas([]);
      return;
    }
    setSchemasLoading(true);
    try {
      const data = await getPimSchemas(tenantId);
      setSchemas(data || []);
    } catch (error) {
      notifications.show({
        color: "red",
        title: t("admin.portability.loadFailed"),
        message: error.message,
      });
      setSchemas([]);
    } finally {
      setSchemasLoading(false);
    }
  };

  useEffect(() => {
    loadSchemas(selectedTenantId);
  }, [selectedTenantId]);

  const handleExport = async (schema) => {
    if (!selectedTenantId) return;
    try {
      const data = await exportPimSchema(schema.id, selectedTenantId);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const safeName = (schema.name || "schema").replace(/[^a-z0-9-_]/gi, "_");
      const a = document.createElement("a");
      a.href = url;
      a.download = `schema-${safeName}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      notifications.show({
        color: "green",
        title: t("admin.portability.exportedTitle"),
        message: t("admin.portability.exportedMessage", { name: schema.name }),
      });
    } catch (error) {
      notifications.show({
        color: "red",
        title: t("admin.portability.exportFailed"),
        message: error.message,
      });
    }
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <div>
          <Title order={2}>{t("admin.portability.title")}</Title>
          <Text c="dimmed" size="sm">
            {t("admin.portability.description")}
          </Text>
        </div>

        <Card withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="flex-end">
              <Select
                label={t("admin.portability.tenantLabel")}
                placeholder={
                  tenantsLoading
                    ? t("admin.portability.loadingTenants")
                    : t("admin.portability.selectTenant")
                }
                data={tenantOptions}
                value={selectedTenantId}
                onChange={setSelectedTenantId}
                searchable
                clearable
                leftSection={<IconBuildingFactory size={16} />}
                disabled={tenantsLoading}
                style={{ minWidth: 320 }}
              />
              <Button
                leftSection={<IconUpload size={16} />}
                disabled={!selectedTenantId}
                onClick={() => setImportOpen(true)}
              >
                {t("admin.portability.importButton")}
              </Button>
            </Group>

            {!selectedTenantId ? (
              <Text c="dimmed" size="sm" ta="center" py="lg">
                {t("admin.portability.pickTenantHint")}
              </Text>
            ) : schemasLoading ? (
              <Center py="lg"><Loader /></Center>
            ) : schemas.length === 0 ? (
              <Text c="dimmed" size="sm" ta="center" py="lg">
                {t("admin.portability.noSchemas")}
              </Text>
            ) : (
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("admin.portability.colName")}</Table.Th>
                    <Table.Th>{t("admin.portability.colKind")}</Table.Th>
                    <Table.Th ta="center">{t("admin.portability.colProductTypes")}</Table.Th>
                    <Table.Th ta="center">{t("admin.portability.colAttributes")}</Table.Th>
                    <Table.Th ta="right">{t("admin.portability.colActions")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {schemas.map((schema) => (
                    <Table.Tr key={schema.id}>
                      <Table.Td>
                        <Text fw={500} size="sm">{schema.labels?.en || schema.name}</Text>
                        <Text size="xs" c="dimmed">{schema.id}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="sm" variant="light">{schema.kind}</Badge>
                      </Table.Td>
                      <Table.Td ta="center">{schema.productTypeCount}</Table.Td>
                      <Table.Td ta="center">{schema.attributeDefinitionCount}</Table.Td>
                      <Table.Td>
                        <Group gap="xs" justify="flex-end">
                          <Tooltip label={t("admin.portability.exportTooltip")}>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              onClick={() => handleExport(schema)}
                            >
                              <IconDownload size={16} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Stack>
        </Card>
      </Stack>

      <ImportSchemaModal
        opened={importOpen}
        onClose={() => setImportOpen(false)}
        targetTenantId={selectedTenantId}
        onImported={() => loadSchemas(selectedTenantId)}
      />
    </Container>
  );
}
